import { Button, Stack, Typography } from '@mui/material'
import { FC, MouseEventHandler, useMemo } from 'react'
import { PointDisplay } from 'utoniq-ui'

import {
  Maybe,
  PreSale,
  SalesCondition,
  SalesStatus,
  StoreItemDetailFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { QuantityPicker } from '@/components/organisms'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

export type StoreButtonBoxProps = {
  label: string
  disabled: boolean
  item: StoreItemDetailFragment
  onAddToCart: MouseEventHandler<HTMLButtonElement>
  onBuyNow: MouseEventHandler<HTMLButtonElement>
  purchaseNumber: number
  maximumPurchasableNumber: number
  formattedItemPrice: string
  purchaseLimitByUser: Maybe<number> | undefined
  purchaseLimitPerOrder: Maybe<number> | undefined
  onChangePurchaseNumber: (value: number) => void
  paymentType: Maybe<UserPaymentType> | undefined
  loading: boolean
  skuId?: string
  willResaleAvailable?: boolean
}

export const StoreButtonBox: FC<StoreButtonBoxProps> = ({
  label,
  disabled,
  item,
  onBuyNow,
  onAddToCart,
  formattedItemPrice,
  onChangePurchaseNumber,
  purchaseNumber,
  maximumPurchasableNumber,
  purchaseLimitPerOrder,
  purchaseLimitByUser,
  loading,
  skuId,
  willResaleAvailable,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { generatePath, routerPages } = useRouter()

  const color = useMemo(() => {
    if (item.salesStatus === SalesStatus.Stopped) {
      return 'text.primary'
    }
    return (item.remainingNumber || 0) < 10 ? 'error' : 'text.primary'
  }, [item.remainingNumber, item.salesStatus])

  const isSoldOut = useMemo(() => {
    const sku = item.skuSettings?.units?.find(sku => sku.id === skuId)
    if (!sku) {
      return !!item.issuedNumber && item.remainingNumber === 0
    }
    if (!item.issuedNumber) return false
    if (!item.isResaleAvailable) {
      return (
        (sku.stock ?? 0) <=
        (sku.purchasedNumber ?? 0) + (sku.pendingPurchaseNumber ?? 0)
      )
    }
    return !!item.issuedNumber && item.remainingNumber === 0
  }, [
    item.isResaleAvailable,
    item.issuedNumber,
    item.remainingNumber,
    item.skuSettings?.units,
    skuId,
  ])

  // 購入制限の表記
  const purchaseLimitText =
    !!purchaseLimitPerOrder && purchaseLimitByUser
      ? t('purchaseLimitTextBoth', {
          purchaseLimitByUser,
          purchaseLimitPerOrder,
        })
      : purchaseLimitPerOrder
      ? t('purchaseLimitTextOrder', { purchaseLimitPerOrder })
      : purchaseLimitByUser
      ? t('purchaseLimitTextUser', { purchaseLimitByUser })
      : ''

  if (
    // リセール可能で、期間中の場合
    (item.isResaleAvailable ||
      // リセール期間前のリセールアイテム
      willResaleAvailable) &&
    !!item
  ) {
    return null
  }

  if (item.preSale) {
    const href = item.preSale.firstComeFirstServed
      ? generatePath(routerPages.FirstComeFirstServedDetail, {
          preSaleId: item.preSale.id,
        })
      : generatePath(routerPages.LotteryDetail, {
          preSaleId: item.preSale.id,
        })
    return (
      <Stack sx={{ py: 3, px: 5 }} spacing={2}>
        <Button fullWidth size="large" color="primary" href={href}>
          {t('seePreSale', '先行販売を見る')}
        </Button>
      </Stack>
    )
  }

  // リセール可能でない場合の表示
  return (
    <Stack sx={{ py: 3, px: 5 }} spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ minHeight: 44 }}
      >
        <Stack flex={1} spacing={1}>
          {formattedItemPrice !== '0' && (
            <PointDisplay
              point={formattedItemPrice ?? 0}
              custom={{ fontSize: 'medium', variant: 'h2', spacing: 2 }}
              paymentType={item.paymentType ?? 'prepaid_point'}
            />
          )}
          {!item.preSale &&
            (item.issuedNumber || 0) > 0 &&
            !item.hideSalesNumber && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('remaining', '残り')}
                </Typography>
                <Typography variant="body2" color={color}>
                  {item.salesStatus === SalesStatus.Stopped
                    ? '-'
                    : item.remainingNumber}
                  /{item.issuedNumber}
                </Typography>
              </Stack>
            )}
        </Stack>

        <Stack spacing={2} alignItems="flex-end">
          {/* 購入数の選択フィールド、購入制限の数まで表示、なければ10個まで */}
          <QuantityPicker
            error={disabled}
            value={purchaseNumber}
            onMinus={() => onChangePurchaseNumber(--purchaseNumber)}
            onPlus={() => onChangePurchaseNumber(++purchaseNumber)}
            disabledPlus={maximumPurchasableNumber <= purchaseNumber}
          />
          {(!!item.purchaseLimitByUser || !!item.purchaseLimitPerOrder) && (
            <Typography variant="caption">{purchaseLimitText}</Typography>
          )}
        </Stack>
      </Stack>

      <ButtonGroupCell
        onAddToCart={onAddToCart}
        label={label}
        disabled={disabled}
        loading={loading}
        onBuyNow={onBuyNow}
        sellingStatus={item.salesCondition}
        isSoldOut={isSoldOut}
        preSaleId={(item.preSale as unknown as PreSale)?.id}
        purchaseNumber={purchaseNumber}
        itemId={item.id ?? ''}
        skuId={skuId}
        formattedItemPrice={formattedItemPrice}
        isEventPickUp={!!item?.collection?.isEventPickup}
      />
    </Stack>
  )
}

type ButtonGroupCellProps = {
  disabled: StoreButtonBoxProps['disabled']
  loading: StoreButtonBoxProps['loading']
  label: StoreButtonBoxProps['label']
  onBuyNow: StoreButtonBoxProps['onBuyNow']
  onAddToCart: StoreButtonBoxProps['onAddToCart']
  sellingStatus: string
  isSoldOut: boolean
  preSaleId?: string | null
  itemId: string
  skuId?: string
  purchaseNumber: number
  formattedItemPrice: string
  isEventPickUp: boolean
}
const ButtonGroupCell: Cell<ButtonGroupCellProps> = ({
  disabled,
  loading,
  label,
  onBuyNow,
  onAddToCart,
  sellingStatus,
  isSoldOut,
  preSaleId,
  itemId,
  skuId,
  purchaseNumber,
  formattedItemPrice,
  isEventPickUp,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { router, generatePath, routerPages } = useRouter()

  const options = useMemo(() => {
    // 発売中以外のパターン
    if (sellingStatus !== SalesCondition.Active) {
      const label = () => {
        switch (sellingStatus) {
          case SalesCondition.Before:
            return t('beforeSelling')
          case SalesCondition.End:
            return t('endSelling')
          case SalesCondition.Stopped:
            return t('stopSelling')
        }
      }
      return [
        {
          label: label(),
          disabled: true,
          loading,
          onClick: () => {},
        },
      ]
    }
    if (isSoldOut) {
      return [
        {
          label: t('soldOut'),
          disabled: true,
          loading,
          onClick: () => {},
        },
      ]
    }
    if (preSaleId) {
      return [
        {
          label: t('applyForLottery'),
          disabled: false,
          loading,
          onClick: () => {
            router.push({
              pathname: generatePath(routerPages.LotteryDetail, {
                preSaleId,
              }),
              search: `?itemId=${itemId}&quantity=${purchaseNumber}${
                skuId ? `&skuId=${encodeSkuId(skuId)}` : ''
              }`,
            })
          },
        },
      ]
    }
    if (config.modules.cart && formattedItemPrice !== '0') {
      // カート機能ONの場合、「カートに追加」「今すぐ購入」の2ボタンが表示
      // https://app.zeplin.io/project/609b6cbdf2ec09be6da76b28/screen/61b8050a169f444d2077ae20
      return [
        {
          label: t('addToCart', 'カートに追加'),
          disabled: disabled || loading,
          loading,
          onClick: onAddToCart,
        },
        // NOTE: 受け取りONのアイテムの場合、「今すぐ購入」のボタンを非表示
        ...(isEventPickUp
          ? []
          : [
              {
                label,
                loading,
                disabled: disabled || loading,
                onClick: onBuyNow,
              },
            ]),
      ]
    }
    return [
      {
        label,
        loading,
        disabled: disabled || loading,
        onClick: onBuyNow,
      },
    ]
  }, [
    sellingStatus,
    isSoldOut,
    preSaleId,
    formattedItemPrice,
    label,
    loading,
    disabled,
    onBuyNow,
    t,
    router,
    generatePath,
    routerPages.LotteryDetail,
    itemId,
    purchaseNumber,
    skuId,
    onAddToCart,
    isEventPickUp,
  ])

  return (
    <Stack direction="row" spacing={2}>
      {options.map(({ label, disabled, loading, onClick }, index, array) => (
        <Button
          key={index}
          fullWidth
          size="large"
          color={'primary'}
          variant={array.length === 2 && index === 0 ? 'outlined' : 'contained'}
          onClick={onClick}
          disabled={loading || disabled}
          loading={loading}
        >
          {label}
        </Button>
      ))}
    </Stack>
  )
}
